/**
 * Home Planner project XML → Scene IR.
 *
 * The conventions encoded here were established against the Unity source and re-verified
 * by the iOS prototype's importer (`HomePlannerLite/tools/import_project.py`). They are not
 * derivable from the XML alone, so changing one is a spec change, not a refactor:
 *
 *   Unity (X, Y, Z)     → IR (x, y, z) = (X, Y, −Z)
 *   Item yaw            = θ° + 180°, in radians
 *   Wall direction      = (cos θ, sin θ) in IR plan space
 *   Wall interior normal= (−sin θ, cos θ)  — the `Front` side faces the room
 *   Item size           = catalog dims × |Scale| (per axis)
 *   Wall item offset    = wallThickness/2 + PlaneOffset + depth/2 along the interior normal
 *   Ceiling collection  is mirrored in X (x → −x, θ → −θ)
 */

import { attr, bool, kid, kids, num, parseXml, path, xy, xyz, type Node } from './xml';
import type {
  CameraState, Item, Level, ModelRef, Module, Opening, RGB, Room, Scene, Surface, Vec2, Wall,
} from './types';
import type { ElementPath } from './projectFile';

const DEG = Math.PI / 180;

/** Unity plan (X, Z) → IR plan (x, z). */
function P(x: number, z: number): Vec2 {
  return { x: r4(x), z: r4(-z) };
}

const r4 = (n: number) => Math.round(n * 1e4) / 1e4;
const r5 = (n: number) => Math.round(n * 1e5) / 1e5;

/** Item yaw: the model's authored front is 180° from the plan's rotation. */
function itemYaw(thetaDeg: number): number {
  let r = thetaDeg * DEG + Math.PI;
  while (r > Math.PI) r -= 2 * Math.PI;
  while (r <= -Math.PI) r += 2 * Math.PI;
  return r5(r);
}

/** Along-wall direction and interior normal, in IR plan space. */
function wallAxes(thetaDeg: number): { dir: Vec2; normalIn: Vec2 } {
  const t = thetaDeg * DEG;
  return {
    dir: { x: Math.cos(t), z: Math.sin(t) },
    normalIn: { x: -Math.sin(t), z: Math.cos(t) },
  };
}

const CLOUD_ID = /^[0-9a-f]{24}$/;

function modelRef(raw: string | undefined): ModelRef {
  const id = (raw ?? '').trim();
  if (!id) return { kind: 'none', id: '', raw: '' };
  if (id.startsWith('Models/')) return { kind: 'local', id: id.slice('Models/'.length), raw: id };
  if (CLOUD_ID.test(id)) return { kind: 'cloud', id, raw: id };
  // Modular sub-parts carry bare layout ids (`h51-w76`); treat them as local without a path.
  return { kind: 'local', id, raw: id };
}

/** The element's `UniqueId` as the file has it, and nothing for an element without one: it is never derived. */
function uniqueIdOf(el: Node | undefined): { uniqueId?: string } {
  const id = attr(el, 'UniqueId');
  return id ? { uniqueId: id } : {};
}

const CUSTOM = '[custom]';

const RES_TEXTURES = /^Assets\/Resources\/Textures\//;
const RES_MODELS = /^Assets\/Resources\/Models\//;

/** `<Material>` / `<MaterialData>` → Surface, with the texture's source classified. */
function surface(m: Node | undefined, owner?: string): Surface | undefined {
  if (!m) return undefined;
  const s: Surface = {};
  // The app reads a path's backslashes as slashes (SerializationHelper.DeserializePathProperty); the file keeps them.
  const raw = attr(m, 'TexturePath') ?? '', tex = raw.replaceAll('\\', '/');
  if (tex.startsWith(CUSTOM)) {
    s.customTexture = tex.slice(CUSTOM.length);
    s.textureKind = 'custom';
  } else if (RES_TEXTURES.test(tex)) {
    s.texture = tex.replace(RES_TEXTURES, '');
    s.textureKind = 'catalog';
  } else if (RES_MODELS.test(tex) && raw === tex) {
    // Slashes only: written the Windows way it stays a picture of the model's archive; the site has one model-tree file.
    s.texture = tex.replace(RES_MODELS, '');
    s.textureKind = 'model';
  } else if (tex.startsWith('Textures/')) {
    s.texture = tex.replace(/^Textures\//, '');
    s.textureKind = 'catalog';
  } else if (tex) {
    // A bare filename: the file ships inside the cloud model's own `source` archive,
    // so it is only resolvable once that archive has been unpacked offline.
    s.texture = tex;
    s.textureKind = 'modelOwn';
    if (owner) s.textureOwner = owner;
  }
  // Width/Height are the tile size in centimetres.
  const w = num(m, 'Width', 0);
  const h = num(m, 'Height', 0);
  if (w > 0 || h > 0) s.tileMeters = [Math.max(w, 5) / 100, Math.max(h || w, 5) / 100];
  const originalW = num(m, 'OriginalWidth', 0), originalH = num(m, 'OriginalHeight', 0);
  if (originalW > 0 && originalH > 0) s.originalTileMeters = [originalW / 100, originalH / 100];
  const rotation = num(m, 'Rotation', 0);
  if (rotation) s.rotation = rotation;
  const til = kid(m, 'Tiling');
  if (til) { const t = xy(til); s.tiling = [t.x, t.y]; }
  const offs = kid(m, 'Offset');
  if (offs) { const o = xy(offs); if (o.x || o.y) s.offset = [o.x, o.y]; }
  const c = kid(m, 'Color');
  if (c) {
    const a = xyzRGBA(c);
    s.color = [a.r, a.g, a.b];
    if (a.a !== 1) s.opacity = a.a;
  }
  const shin = num(m, 'Shininess', -1);
  if (shin >= 0) s.shininess = shin;
  // `MaterialName` is the actual shader; `Type` is the coarser 3ds Max export class
  // (mtl_mesh_colored → mtl_mesh, mtl_steel → mtl_metal) and only a fallback.
  const type = attr(m, 'MaterialName') ?? attr(m, 'Type');
  if (type) s.type = type;
  const cap = attr(m, 'Caption');
  if (cap) s.caption = cap;
  return Object.keys(s).length ? s : undefined;
}

function xyzRGBA(c: Node): { r: number; g: number; b: number; a: number } {
  const a = c['@'] ?? {};
  const g = (k: string, d: number) => {
    const n = Number(a[k]);
    return Number.isFinite(n) ? n : d;
  };
  return { r: g('r', 1), g: g('g', 1), b: g('b', 1), a: g('a', 1) };
}

/** `<Info><Materials>` → per-slot overrides keyed by the Unity material name. */
function instanceMaterials(info: Node | undefined, owner?: string): Record<string, Surface> | undefined {
  const out: Record<string, Surface> = {};
  for (const e of kids(path(info, 'Materials'), 'MaterialEntry')) {
    const key = String(e.Key ?? '').trim();
    const s = surface(kid(e, 'MaterialData'), owner);
    if (key && s) out[key] = s;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Modular furniture (IKEA kitchens) nests three levels deep — cabinet-layout →
 * handle-layout → door — and only the leaves carry geometry. Each level's `<Info>` holds
 * its own transform and materials, so the whole tree has to come across, not just depth 0.
 */
function modules(node: Node | undefined): Module[] | undefined {
  const list = kids(path(node, 'Modules'), 'Module');
  if (!list.length) return undefined;
  return list.map((m) => {
    const info = kid(m, 'Info');
    const ref = modelRef(attr(info, 'FurnitureId') ?? String(m.furnitureId ?? ''));
    const pos = kid(m, 'Position') ?? kid(info, 'Position');
    const rot = kid(m, 'Rotation') ?? kid(info, 'Rotation');
    const out: Module = {
      name: String(m.name ?? ''),
      modelRef: ref,
      variationId: m.variationId ? String(m.variationId) : undefined,
      materials: instanceMaterials(info, ref.id),
      modules: modules(info),
    };
    if (pos) { const p = xyz(pos); out.position = [p.x, p.y, p.z]; }
    if (rot) { const r = xyz(rot); out.rotation = [r.x, r.y, r.z]; }
    return out;
  });
}

/** Every model id in a module tree, leaves included. */
function walkModules(list: Module[] | undefined, visit: (m: Module) => void): void {
  for (const m of list ?? []) { visit(m); walkModules(m.modules, visit); }
}

function scaleOf(el: Node): [number, number, number] {
  const s = kid(el, 'Scale');
  if (!s) return [1, 1, 1];
  const v = xyz(s, 1);
  return [v.x || 1, v.y || 1, v.z || 1];
}

/**
 * Final world size. Width/Height/Depth in the XML are the CATALOG dimensions;
 * the real size is those times |Scale| per axis. For cloud models the XML dims are
 * unreliable (see import_project.py) — `catalogDims` overrides them when supplied.
 */
function finalSize(
  el: Node,
  ref: ModelRef,
  catalogDims?: Map<string, [number, number, number]>,
): { w: number; h: number; d: number; mirrored: boolean; scale: [number, number, number] } {
  const scale = scaleOf(el);
  const base = catalogDims?.get(ref.id) ?? [num(el, 'Width'), num(el, 'Height'), num(el, 'Depth')];
  return {
    w: r4(base[0] * Math.abs(scale[0])),
    h: r4(base[1] * Math.abs(scale[1])),
    d: r4(base[2] * Math.abs(scale[2])),
    mirrored: scale[0] < 0,
    scale,
  };
}

function lightOf(el: Node, caption: string): Item['light'] | undefined {
  const l = kid(el, 'Light');
  if (!l) return undefined;
  if (!bool(l, 'Enabled', true)) return undefined;
  // LED strips and panels are numerous and contribute almost nothing; each one still
  // costs a real-time light, so they are decorative geometry only.
  if (caption.toUpperCase().includes('LED')) return undefined;
  const intensity = num(l, 'LightIntensity', 1);
  if (!intensity) return undefined;
  const c = kid(l, 'Color');
  let color: RGB = [1, 1, 1];
  if (c) {
    const v = xyzRGBA(c);
    // A zero colour means "use the colour temperature"; the app falls back to warm white.
    if (v.r || v.g || v.b) color = [v.r, v.g, v.b];
    else color = kelvinToRGB(num(l, 'ColorTemperature', 4000));
  }
  return { color, intensity, range: num(l, 'Range', 6) || 6 };
}

/** Tanner Helland's approximation, adequate for lamp tinting. */
function kelvinToRGB(k: number): RGB {
  const t = Math.min(Math.max(k, 1000), 40000) / 100;
  const cl = (v: number) => Math.min(Math.max(v, 0), 255) / 255;
  const r = t <= 66 ? 255 : 329.698727446 * Math.pow(t - 60, -0.1332047592);
  const g = t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661
    : 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  return [cl(r), cl(g), cl(b)];
}

function polygonArea(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.z - q.x * p.z;
  }
  return Math.abs(a / 2);
}

interface WallCtx {
  wall: Wall;
  cx: number;
  cz: number;
  theta: number;
  length: number;
  thickness: number;
  forward: boolean;
  dir: Vec2;
  normalIn: Vec2;
}

/** Models whose wall-mounted pivot sits at the mesh bottom rather than its centre. */
const BOTTOM_PIVOT = ['storage.cabinet', 'decor.primitives', 'Imports/6308'];

export interface ParseOptions {
  projectId?: string;
  /** Catalog tags per model id — needed for `AutorotateVerticalToHorizontal`. */
  modelTags?: Map<string, string[]>;
  /** Real cloud-model dimensions (mesh × definition scale) keyed by 24-hex id. */
  catalogDims?: Map<string, [number, number, number]>;
  /** Cloud-model pivot heights (mesh bottom relative to the pivot) keyed by 24-hex id. */
  catalogPivots?: Map<string, number>;
  /** Told of each kept level, room, wall, opening and item with the path of its element in the file (`ProjectFile.element`). */
  onElement?: (object: Level | Room | Wall | Opening | Item, path: ElementPath) => void;
  /**
   * Internal ids from the file instead of document order: from the UniqueId for its first holder, else from the element's
   * path, so a re-read after one object is added or removed leaves every other id as it was.
   */
  stableIds?: boolean;
}

export function parseProject(xmlText: string, opts: ParseOptions = {}): Scene {
  const doc = parseXml(xmlText);
  const root = kid(doc, 'Project');
  if (!root) throw new Error('not a Home Planner project: no <Project> root');

  const warnings: string[] = [];
  const levels: Level[] = [];
  let uid = 0;
  const nextId = (p: string) => `${p}${(++uid).toString(36)}`;
  const seenIds = new Set<string>(), repeatedIds = new Set<string>(), stableTaken = new Set<string>();
  // Runs before the object's dependants read its id: a wall is kept before its openings and wall items take `ctx.wall.id`.
  const keep = <T extends Level | Room | Wall | Opening | Item>(object: T, at: ElementPath): T => {
    const id = object.uniqueId;
    if (id && seenIds.has(id) && !repeatedIds.has(id)) { repeatedIds.add(id); warnings.push(`duplicate UniqueId ${id}`); }
    if (id) seenIds.add(id);
    if (opts.stableIds && 'id' in object) {
      const prefix = object.id.charAt(0), fromId = id && `${prefix}:${id}`;
      object.id = fromId && !stableTaken.has(fromId) ? fromId : `${prefix}@${at.map(([tag, index]) => `${tag}${index}`).join('/')}`;
      stableTaken.add(object.id);
    }
    opts.onElement?.(object, at);
    return object;
  };

  const levelNodes = kids(path(root, 'Building/Levels'), 'Level');
  // Templates under NewProjects use the old flat shape: <Project><Room>.
  const flatRooms = kids(root, 'Room');
  const sources = levelNodes.length ? levelNodes : flatRooms.length ? [root] : [];
  if (!sources.length) warnings.push('project has no levels and no rooms');

  sources.forEach((levelNode, levelIndex) => {
    const levelPath: ElementPath = levelNodes.length ? [['Building', 0], ['Levels', 0], ['Level', levelIndex]] : [];
    const roomNodes = levelNodes.length ? kids(path(levelNode, 'Rooms'), 'Room') : flatRooms;
    const levelHeight = num(levelNode, 'Height', 0)
      || num(roomNodes[0], 'Height', 3)
      || 3;

    const walls: Wall[] = [];
    const openings: Opening[] = [];
    const items: Item[] = [];
    const rooms: Room[] = [];

    roomNodes.forEach((roomNode, roomIndex) => {
      const roomPath: ElementPath = levelNodes.length ? [...levelPath, ['Rooms', 0], ['Room', roomIndex]] : [['Room', roomIndex]];
      const poly = kids(path(roomNode, 'Floor/Shape'), 'Vector2').map((v) => {
        const p = xy(v);
        return P(p.x, p.y);
      });
      const wallNodes = kids(path(roomNode, 'Walls'), 'Wall');
      const n = poly.length;

      wallNodes.forEach((w, i) => {
        const wallPath: ElementPath = [...roomPath, ['Walls', 0], ['Wall', i]];
        const theta = num(w, 'Rotation');
        let length = num(w, 'Width');
        let thickness = num(w, 'Depth', 0.2) || 0.2;
        const pos = kid(w, 'Position');
        let cx: number;
        let cz: number;
        let th = theta;

        if (!pos && n === wallNodes.length) {
          // Old template shape: the wall IS edge i of the room outline.
          const pa = poly[i];
          const pb = poly[(i + 1) % n];
          cx = (pa.x + pb.x) / 2;
          cz = -(pa.z + pb.z) / 2; // back to Unity Z for the shared maths below
          length = Math.hypot(pb.x - pa.x, pb.z - pa.z);
          th = Math.atan2(pb.z - pa.z, pb.x - pa.x) / DEG;
          thickness = num(w, 'Thickness', 0.2) || 0.2;
        } else {
          const p = xy(pos);
          cx = p.x;
          cz = p.y;
        }

        const { dir, normalIn } = wallAxes(th);
        const centre = P(cx, cz);

        // Prefer the room outline's own endpoints — they carry the mitred corners.
        let a: Vec2 | null = null;
        let b: Vec2 | null = null;
        let forward = true;
        if (n === wallNodes.length) {
          const pa = poly[i];
          const pb = poly[(i + 1) % n];
          const seg = { x: pb.x - pa.x, z: pb.z - pa.z };
          const segLen = Math.hypot(seg.x, seg.z) || 1;
          const dot = (seg.x * dir.x + seg.z * dir.z) / segLen;
          const midOk = Math.hypot((pa.x + pb.x) / 2 - centre.x, (pa.z + pb.z) / 2 - centre.z) < 0.02;
          if (Math.abs(dot) > 0.99 && midOk && Math.abs(segLen - length) < 0.02) {
            a = pa;
            b = pb;
            forward = dot > 0;
          }
        }
        if (!a || !b) {
          a = { x: r4(centre.x - dir.x * length / 2), z: r4(centre.z - dir.z * length / 2) };
          b = { x: r4(centre.x + dir.x * length / 2), z: r4(centre.z + dir.z * length / 2) };
          if (n === wallNodes.length) {
            warnings.push(`wall ${i} of room ${roomIndex}: outline mismatch, endpoints derived from Position/Rotation`);
          }
        }

        const front = kid(w, 'Front') ?? w;
        const sidePath: ElementPath = front === w ? wallPath : [...wallPath, ['Front', 0]];
        const back = kid(w, 'Back');
        // Skirting and molding hang off the wall SIDE, not the wall. Reading them from
        // `<Wall>` silently drops every one of them.
        const plinthEl = kid(front, 'Plinth');
        const moldingEl = kid(front, 'CeiliningMolding');

        const wall: Wall = {
          id: nextId('w'),
          ...uniqueIdOf(w),
          start: a,
          end: b,
          length: r4(length),
          thickness: r4(thickness),
          height: r4(num(front, 'Height', levelHeight) || levelHeight),
          theta: th,
          dir: { x: r5(dir.x), z: r5(dir.z) },
          normalIn: { x: r5(normalIn.x), z: r5(normalIn.z) },
          center: centre,
          forward,
          frontSurface: surface(kid(front, 'Material')),
          backSurface: surface(kid(back, 'Material')),
          // Absent element means hidden; present with no `IsHidden` means visible.
          plinth: plinthEl && !bool(plinthEl, 'IsHidden')
            ? { height: num(plinthEl, 'Height', 0.15), depth: num(plinthEl, 'Depth', 0.01), surface: surface(kid(plinthEl, 'Material')) }
            : undefined,
          molding: moldingEl && !bool(moldingEl, 'IsHidden')
            ? { height: num(moldingEl, 'Height', 0.1), depth: num(moldingEl, 'Depth', 0.01), surface: surface(kid(moldingEl, 'Material')) }
            : undefined,
          hidden: bool(kid(w, 'wall'), 'IsHidden'),
          roomIndex,
        };
        walls.push(keep(wall, wallPath));

        const ctx: WallCtx = {
          wall, cx, cz, theta: th, length, thickness, forward, dir, normalIn,
        };

        kids(path(front, 'Openings'), 'Opening').forEach((o, k) => {
          const op = parseOpening(o, ctx, nextId, opts);
          if (op) openings.push(keep(op, [...sidePath, ['Openings', 0], ['Opening', k]]));
        });
        kids(path(front, 'FurnitureCollection'), 'Furniture').forEach((fu, k) => {
          const it = parseWallFurniture(fu, ctx, nextId, opts);
          if (it) items.push(keep(it, [...sidePath, ['FurnitureCollection', 0], ['Furniture', k]]));
        });
      });

      const floorMat = surface(path(roomNode, 'Floor/Material'));
      rooms.push(keep({
        index: roomIndex,
        ...uniqueIdOf(roomNode),
        name: (attr(roomNode, 'RoomName') ?? '').trim(),
        roomType: (attr(roomNode, 'RoomType') ?? '').trim(),
        polygon: poly,
        area: r4(polygonArea(poly)),
        floorSurface: floorMat,
        ceilingSurface: surface(path(roomNode, 'Ceiling/Material')),
        height: levelHeight,
      }, roomPath));

      kids(path(roomNode, 'Floor/FurnitureCollection'), 'Furniture').forEach((fu, k) => {
        const it = parseFurniture(fu, 'floor', levelHeight, nextId, opts);
        if (it) items.push(keep(it, [...roomPath, ['Floor', 0], ['FurnitureCollection', 0], ['Furniture', k]]));
      });
      kids(path(roomNode, 'Ceiling/FurnitureCollection'), 'Furniture').forEach((fu, k) => {
        const it = parseFurniture(fu, 'ceiling', levelHeight, nextId, opts);
        if (it) items.push(keep(it, [...roomPath, ['Ceiling', 0], ['FurnitureCollection', 0], ['Furniture', k]]));
      });
    });

    levels.push(keep({
      index: levelIndex,
      ...uniqueIdOf(levelNode),
      name: attr(levelNode, 'LevelName') ?? '',
      lowerLevel: bool(levelNode, 'IsLowerLevel'),
      height: levelHeight,
      outline: kids(path(levelNode, 'Floor/Shape'), 'Vector2').map((v) => {
        const p = xy(v);
        return P(p.x, p.y);
      }),
      rooms,
      walls,
      openings,
      items,
    }, levelPath));
  });

  const camNode = kid(root, 'CameraPosition');
  let camera: CameraState | undefined;
  if (camNode) {
    const p = xyz(kid(camNode, 'Position'));
    camera = {
      position: P(p.x, p.z),
      height: r4(p.y || 1.65),
      yaw: r5(Math.PI - num(camNode, 'HorizontalAngle') * DEG),
      pitch: r5(-num(camNode, 'VerticalAngle') * DEG),
      fovHorizontal: num(camNode, 'FieldOfView', 90),
      view: attr(root, 'ViewName') ?? 'WalkView',
    };
  }

  const scene: Scene = {
    projectId: opts.projectId,
    culture: attr(root, 'Culture') ?? 'en-US',
    revision: num(root, 'Revision', 0),
    appVersion: attr(root, 'AppVersion') ?? '',
    roomType: attr(root, 'RoomType') ?? '',
    styles: (attr(root, 'Styles') ?? '').split(',').filter(Boolean),
    levels,
    camera,
    assets: { models: [], textures: [], customTextures: [], modelTextures: [] },
    warnings,
  };
  collectAssets(scene);
  return scene;
}

/**
 * An item's own `<Material>` is its primary finish; `<Info><Materials>` names individual
 * slots (a primitive's six box faces are literally `front`/`back`/`top`/…). Both appear
 * together, so the direct one becomes the wildcard the named slots fall back to — using
 * either alone leaves primitives like countertops and backsplashes untextured.
 */
function mergeFinishes(
  named: Record<string, Surface> | undefined,
  own: Surface | undefined,
): Record<string, Surface> | undefined {
  if (!own) return named;
  return { ...(named ?? {}), '*': own };
}

function parseFurniture(
  el: Node,
  placement: 'floor' | 'ceiling',
  levelHeight: number,
  nextId: (p: string) => string,
  opts: ParseOptions,
): Item | null {
  const info = kid(el, 'Info');
  const ref = modelRef(attr(info, 'FurnitureId'));
  const p = xy(kid(el, 'Position'));
  const size = finalSize(el, ref, opts.catalogDims);
  let { w, h, d } = size;
  if (w <= 0 || h <= 0 || d <= 0) return null;

  const tags = opts.modelTags?.get(ref.id);
  let x = p.x;
  let theta = num(el, 'Rotation');
  let elevation = num(el, 'PlaneOffset');

  if (placement === 'ceiling') {
    // The ceiling plane is the floor flipped over: X and the rotation are mirrored.
    x = -x;
    theta = -theta;
    // These models are authored standing up and get laid flat against the ceiling.
    // The saved scalar `Rotation` cannot encode that out-of-plane turn, so it is
    // applied here — otherwise a 1.6 cm LED strip becomes a 2.7 m vertical post.
    if (tags?.includes('AutorotateVerticalToHorizontal')) [h, d] = [d, h];
    elevation = Math.max(levelHeight - h - num(el, 'PlaneOffset'), 0);
  }

  const own = surface(kid(el, 'Material'), ref.id);
  const materials = mergeFinishes(instanceMaterials(info, ref.id), own);
  // A flat, wide, near-zero-height box is a rug or a floor decal: give it enough
  // thickness not to z-fight the floor it lies on.
  if (placement === 'floor' && h < 0.03 && w > 0.8) h = Math.max(h, 0.012);

  const caption = attr(info, 'Caption') ?? '';
  return {
    id: nextId('i'),
    ...uniqueIdOf(el),
    modelRef: ref,
    caption: caption || undefined,
    placement,
    position: P(x, p.y),
    elevation: r4(elevation),
    rotation: itemYaw(theta),
    size: { w, h, d },
    scale: size.scale,
    mirrored: size.mirrored,
    ...lockedOf(el),
    pivot: 'bottom',
    materials,
    light: lightOf(el, caption),
    modules: modules(info),
    variationId: attr(info, 'Id') ?? undefined,
    price: numOrUndef(attr(info, 'Price')),
    url: attr(info, 'Url'),
    tags,
    usesExplicitTiling: usesExplicitTiling(ref, tags),
  };
}

/** `Furniture.Locked`, written only when true; Deserialize reads it before Scale, so an absent one is false. */
const lockedOf = (el: Node): { locked?: true } => bool(el, 'Locked') ? { locked: true } : {};

/**
 * Unity applies a material's explicit `<Tiling>` only to meshes whose UVs already span
 * 0..1 — primitives, openings, plinths and moldings. Everywhere else it derives the
 * tiling from the texture's size in centimetres instead.
 */
function usesExplicitTiling(ref: ModelRef, tags: string[] | undefined): boolean {
  return ref.raw.includes('decor.primitives')
    || ref.raw.includes('wall.plinths')
    || ref.raw.includes('wall.doors')
    || ref.raw.includes('wall.windows')
    || !!tags?.includes('Primitive');
}

/**
 * What a wall item's elevation adds to the file's pivot height Y, and which point of the item that elevation measures.
 * A cloud model (a flat user asset such as a picture sticker) hangs by its bottom edge, as do the bottom-pivot models.
 */
export function wallPivotLift(
  ref: ModelRef,
  h: number,
  scaleY: number,
  pivots?: ReadonlyMap<string, number>,
): { lift: number; pivot: Item['pivot'] } {
  const pivotY = pivots?.get(ref.id);
  if (pivotY !== undefined) return { lift: pivotY * Math.abs(scaleY), pivot: 'bottom' };
  if (ref.kind === 'cloud' || BOTTOM_PIVOT.some((s) => ref.raw.includes(s))) return { lift: 0, pivot: 'bottom' };
  return { lift: -h / 2, pivot: 'center' };
}

function parseWallFurniture(
  el: Node,
  ctx: WallCtx,
  nextId: (p: string) => string,
  opts: ParseOptions,
): Item | null {
  const info = kid(el, 'Info');
  const ref = modelRef(attr(info, 'FurnitureId'));
  const p = xy(kid(el, 'Position'));
  const along = p.x;      // distance along the wall from its centre
  const heightY = p.y;    // pivot height above the floor
  const size = finalSize(el, ref, opts.catalogDims);
  let { w, h } = size;
  const { d } = size;

  const inPlane = num(el, 'Rotation');
  if (Math.abs((((inPlane % 180) + 180) % 180) - 90) < 1) {
    // Rotated 90° within the wall plane: width and height swap.
    [w, h] = [h, w];
  }
  if (w <= 0 || h <= 0 || d <= 0) return null;

  const { lift, pivot } = wallPivotLift(ref, h, size.scale[1], opts.catalogPivots);
  const elevation = Math.min(Math.max(heightY + lift, 0), Math.max(ctx.wall.height - h, 0));

  const off = ctx.thickness / 2 + num(el, 'PlaneOffset') + d / 2;
  const cx = ctx.wall.center.x + ctx.dir.x * along + ctx.normalIn.x * off;
  const cz = ctx.wall.center.z + ctx.dir.z * along + ctx.normalIn.z * off;

  const mats = mergeFinishes(instanceMaterials(info, ref.id), surface(kid(el, 'Material'), ref.id));
  const tags = opts.modelTags?.get(ref.id);
  const caption = attr(info, 'Caption') ?? '';
  return {
    id: nextId('i'),
    ...uniqueIdOf(el),
    modelRef: ref,
    caption: caption || undefined,
    placement: 'wall',
    position: { x: r4(cx), z: r4(cz) },
    elevation: r4(elevation),
    rotation: itemYaw(ctx.theta + 180),
    size: { w, h, d },
    scale: size.scale,
    mirrored: size.mirrored,
    ...lockedOf(el),
    pivot,
    materials: mats,
    light: lightOf(el, caption),
    modules: modules(info),
    wallId: ctx.wall.id,
    variationId: attr(info, 'Id') ?? undefined,
    price: numOrUndef(attr(info, 'Price')),
    url: attr(info, 'Url'),
    tags,
    usesExplicitTiling: usesExplicitTiling(ref, tags),
  };
}

function parseOpening(
  el: Node,
  ctx: WallCtx,
  nextId: (p: string) => string,
  opts: ParseOptions,
): Opening | null {
  const info = kid(el, 'Info');
  const ref = modelRef(attr(info, 'FurnitureId'));
  const p = xy(kid(el, 'Position'));
  const along = p.x;
  const sillRaw = Math.max(p.y, 0);
  const size = finalSize(el, ref, opts.catalogDims);
  const width = Math.min(size.w, ctx.length - 0.1);
  if (width <= 0 || size.h <= 0) return null;

  // `offset` measures from the wall's `start`; `along` measures from its centre,
  // signed along the authored direction.
  let offset = ctx.forward ? ctx.length / 2 + along : ctx.length / 2 - along;
  offset = Math.min(Math.max(offset, width / 2 + 0.02), ctx.length - width / 2 - 0.02);
  const sill = sillRaw < 0.02 ? 0 : sillRaw;

  return {
    id: nextId('o'),
    ...uniqueIdOf(el),
    wallId: ctx.wall.id,
    modelRef: ref,
    offset: r4(offset),
    width: r4(width),
    height: r4(Math.min(size.h, ctx.wall.height - sill - 0.05)),
    sill: r4(sill),
    flipped: Math.abs(num(el, 'Rotation') - 180) < 1,
    materials: instanceMaterials(info, ref.id),
    caption: attr(info, 'Caption') ?? undefined,
    variationId: attr(info, 'Id') ?? undefined,
    view: surface(kid(el, 'View')),
  };
}

function numOrUndef(v: string | undefined): number | undefined {
  if (v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function collectAssets(scene: Scene): void {
  const models = new Map<string, ModelRef>();
  const textures = new Set<string>();
  const custom = new Set<string>();
  const modelOwn = new Set<string>();

  const takeSurface = (s?: Surface) => {
    if (!s) return;
    if (s.customTexture) custom.add(s.customTexture);
    else if (s.texture && (s.textureKind === 'catalog' || s.textureKind === 'model')) textures.add(`${s.textureKind}:${s.texture}`);
    else if (s.texture) modelOwn.add(`${s.textureOwner ?? '?'}/${s.texture}`);
  };
  const takeMats = (m?: Record<string, Surface>) => {
    if (m) for (const s of Object.values(m)) takeSurface(s);
  };
  const takeModel = (r: ModelRef) => {
    if (r.kind !== 'none' && !models.has(r.raw)) models.set(r.raw, r);
  };

  for (const level of scene.levels) {
    for (const r of level.rooms) {
      takeSurface(r.floorSurface);
      takeSurface(r.ceilingSurface);
    }
    for (const w of level.walls) {
      takeSurface(w.frontSurface);
      takeSurface(w.backSurface);
      takeSurface(w.plinth?.surface);
      takeSurface(w.molding?.surface);
    }
    for (const o of level.openings) {
      takeModel(o.modelRef);
      takeMats(o.materials);
      takeSurface(o.view);          // the street view seen through the opening
    }
    for (const it of level.items) {
      takeModel(it.modelRef);
      takeMats(it.materials);
      walkModules(it.modules, (m) => { takeModel(m.modelRef); takeMats(m.materials); });
    }
  }

  scene.assets.models = [...models.values()];
  scene.assets.textures = [...textures].sort();
  scene.assets.customTextures = [...custom].sort();
  scene.assets.modelTextures = [...modelOwn].sort();
}
